/** Shared calendar + attendance helpers (admin & employee dashboards) */

export const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

export const WEEKDAYS_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export const PUBLIC_HOLIDAYS = [
    { month: 0, day: 1, name: "New Year's Day" },
    { month: 0, day: 26, name: "Republic Day" },
    { month: 2, day: 14, name: "Holi" },
    { month: 3, day: 3, name: "Good Friday" },
    { month: 4, day: 1, name: "May Day" },
    { month: 7, day: 15, name: "Independence Day" },
    { month: 9, day: 2, name: "Gandhi Jayanti" },
    { month: 9, day: 22, name: "Dussehra" },
    { month: 10, day: 1, name: "Diwali" },
    { month: 11, day: 25, name: "Christmas Day" },
];

export const toDateKey = (year, month, day) =>
    `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const getHolidayDateKeys = (holiday) => {
    const keys = new Set();
    if (holiday?.dateKey) keys.add(String(holiday.dateKey).slice(0, 10));
    if (!holiday?.date) return [...keys].filter(Boolean);
    const rawDate = String(holiday.date);
    if (/^\d{4}-\d{2}-\d{2}/.test(rawDate)) keys.add(rawDate.slice(0, 10));
    const parsedDate = new Date(holiday.date);
    if (!Number.isNaN(parsedDate.getTime())) {
        keys.add(toDateKey(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()));
    }
    return [...keys].filter(Boolean);
};

export const isLeaveApproved = (leave) => {
    const s = (leave?.status || "").toLowerCase();
    return s === "approved";
};

export const normalizeLeaveType = (leaveType) => {
    const type = String(leaveType || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
    if (["compensatory", "compoff", "compensatoryoff", "compensatoryleave"].includes(type)) return "compensatory";
    if (["medical", "medicalleave", "sickleave"].includes(type)) return "sick";
    if (["casualleave"].includes(type)) return "casual";
    if (["lossofpay", "losspay", "lop"].includes(type)) return "lop";
    if (["emergencyleave"].includes(type)) return "emergency";
    return type || "other";
};

export const getLeaveForDate = (leaves, day, month, year) => {
    const checkDate = new Date(year, month, day);
    checkDate.setHours(12, 0, 0, 0);

    return (leaves || []).find((l) => {
        if (!isLeaveApproved(l)) return false;
        const start = new Date(l.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(l.endDate);
        end.setHours(23, 59, 59, 999);
        return checkDate >= start && checkDate <= end;
    });
};

/** Dates (YYYY-MM-DD) where employee checked in */
export const buildPresentDateSet = (attendance = []) => {
    const set = new Set();
    (attendance || []).forEach((record) => {
        if (String(record?.status || "").toLowerCase() === "absent") return;
        if (isHolidayAttendanceRecord(record)) return;
        if (!record?.checkIn) return;
        if (record.date) {
            set.add(record.date);
            return;
        }
        const d = new Date(record.checkIn);
        if (!Number.isNaN(d.getTime())) {
            set.add(toDateKey(d.getFullYear(), d.getMonth(), d.getDate()));
        }
    });
    return set;
};

const getAttendanceDateKey = (record) => {
    if (record?.date) return record.date;
    if (!record?.checkIn) return "";
    const d = new Date(record.checkIn);
    if (Number.isNaN(d.getTime())) return "";
    return toDateKey(d.getFullYear(), d.getMonth(), d.getDate());
};

const isHolidayAttendanceRecord = (record) => {
    const status = String(record?.status || "").toLowerCase();
    const workHours = String(record?.workHours || "").toLowerCase();
    return status === "holiday" || workHours.startsWith("holiday");
};

const getAttendanceHolidayName = (record) => {
    const workHours = String(record?.workHours || "").trim();
    return workHours.toLowerCase().startsWith("holiday")
        ? workHours.replace(/^holiday\s*-?\s*/i, "").trim() || "Public Holiday"
        : "Public Holiday";
};

export const getAttendanceStartDateKey = (attendance = [], attendanceStartDate) => {
    if (attendanceStartDate) {
        const explicitDate = new Date(attendanceStartDate);
        if (!Number.isNaN(explicitDate.getTime())) {
            return toDateKey(explicitDate.getFullYear(), explicitDate.getMonth(), explicitDate.getDate());
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(String(attendanceStartDate))) {
            return String(attendanceStartDate);
        }
    }

    return (attendance || [])
        .filter((record) => record?.checkIn)
        .map(getAttendanceDateKey)
        .filter(Boolean)
        .sort()[0] || "";
};

/**
 * Per-day flags + monthly counts.
 * Not In = working day in the past with no check-in and no approved leave.
 */
export const computeMonthAttendanceStats = (
    year,
    month,
    { leaves = [], attendance = [], holidays = [], today = new Date(), attendanceStartDate } = {}
) => {
    const presentSet = buildPresentDateSet(attendance);
    const attendanceStartKey = getAttendanceStartDateKey(attendance, attendanceStartDate);
    const publicHolidaysByDate = new Map();
    (holidays || []).forEach((holiday) => {
        getHolidayDateKeys(holiday).forEach((key) => {
            publicHolidaysByDate.set(key, holiday);
        });
    });
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    let presentCount = 0;
    let notInCount = 0;
    let compensatedCount = 0;
    let weekOffCount = 0;
    let holidayCount = 0;
    const dayMeta = {};

    for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(year, month, d);
        const isSunday = dateObj.getDay() === 0;
        const isWeekOff = isSunday;
        const staticHoliday = PUBLIC_HOLIDAYS.find((h) => h.month === month && h.day === d);
        // Leave approval is permission only; attendance is changed manually by an admin.
        const leave = null;
        const dateKey = toDateKey(year, month, d);
        const attendanceRecord = (attendance || []).find((record) => record?.date === dateKey);
        const attendanceHolidayName = isHolidayAttendanceRecord(attendanceRecord) ? getAttendanceHolidayName(attendanceRecord) : "";
        const configuredHoliday = publicHolidaysByDate.get(dateKey);
        const holiday = attendanceHolidayName ? { name: attendanceHolidayName } : configuredHoliday || staticHoliday;
        const isExplicitAbsent = String(attendanceRecord?.status || "").toLowerCase() === "absent";
        const isCompensated = String(attendanceRecord?.status || "").toLowerCase() === "compensated";
        const hasCheckIn = presentSet.has(dateKey) && !isExplicitAbsent;
        const isPast = dateObj < todayStart;
        const isBeforeAttendanceStart = attendanceStartKey && dateKey < attendanceStartKey;

        const meta = {
            dateKey,
            attendanceStartKey,
            isBeforeAttendanceStart,
            isSunday,
            isWeekOff,
            isHoliday: !!holiday,
            holidayName: holiday?.name || "",
            leave,
            hasCheckIn,
            isPresent: false,
            isNotIn: false,
            isExplicitAbsent,
            isCompensated,
            compensationType: attendanceRecord?.compensationType || "",
        };

        if (isBeforeAttendanceStart) {
            /* employee attendance had not started yet */
        } else if (holiday) {
            holidayCount++;
        } else if (hasCheckIn) {
            presentCount++;
            meta.isPresent = true;
        } else if (isCompensated) {
            compensatedCount++;
        } else if (leave) {
            /* approved leave day */
        } else if (isExplicitAbsent) {
            notInCount++;
            meta.isNotIn = true;
        } else if (isWeekOff) {
            weekOffCount++;
        } else if (isPast) {
            notInCount++;
            meta.isNotIn = true;
        }

        dayMeta[d] = meta;
    }

    return {
        presentCount,
        notInCount,
        compensatedCount,
        weekOffCount,
        holidayCount,
        presentSet,
        dayMeta,
        daysInMonth,
    };
};

export const countLeavesInMonth = (leaves, year, month) => {
    let casualCount = 0;
    let sickCount = 0;
    let lopCount = 0;
    let compensatoryCount = 0;
    let emergencyCount = 0;
    let otherCount = 0;

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
        const leave = getLeaveForDate(leaves, d, month, year);
        if (!leave) continue;
        const leaveType = normalizeLeaveType(leave.leaveType);
        if (leaveType === "casual") casualCount++;
        else if (leaveType === "sick") sickCount++;
        else if (leaveType === "lop") lopCount++;
        else if (leaveType === "compensatory") compensatoryCount++;
        else if (leaveType === "emergency") emergencyCount++;
        else otherCount++;
    }

    return {
        casualCount,
        sickCount,
        lopCount,
        compensatoryCount,
        emergencyCount,
        otherCount,
        totalLeaveDays:
            casualCount + sickCount + lopCount + compensatoryCount + emergencyCount + otherCount,
    };
};

export const computeAutoLeaveCompensation = () => {
    const counts = {
        carryForward: 0,
        casual: 0,
        medical: 0,
        compensatory: 0,
        lossOfPay: 0,
    };
    return {
        allocationByDate: {},
        counts,
        compensatedCount: 0,
        payableAbsentCount: 0,
    };
};

/** Unique colour per calendar status (cells + legend dots) */
export const CALENDAR_COLORS = {
    today: {
        dot: "bg-sky-500",
        cell: "bg-sky-500 text-white shadow-md shadow-sky-500/30 font-bold",
    },
    weekOff: {
        dot: "bg-orange-500",
        cell: "bg-orange-500 text-white shadow-md shadow-orange-500/30 font-bold",
    },
    holiday: {
        dot: "bg-fuchsia-600",
        cell: "bg-fuchsia-600 text-white shadow-md shadow-fuchsia-600/30 font-bold",
    },
    casual: {
        dot: "bg-blue-600",
        cell: "bg-blue-600 text-white shadow-md shadow-blue-600/30 font-bold",
    },
    sick: {
        dot: "bg-indigo-600",
        cell: "bg-indigo-600 text-white shadow-md shadow-indigo-600/30 font-bold",
    },
    lop: {
        dot: "bg-rose-600",
        cell: "bg-rose-600 text-white shadow-md shadow-rose-600/30 font-bold",
    },
    compensatory: {
        dot: "bg-violet-600",
        cell: "bg-violet-600 text-white shadow-md shadow-violet-600/30 font-bold",
    },
    emergency: {
        dot: "bg-amber-500",
        cell: "bg-amber-500 text-neutral-900 shadow-md shadow-amber-500/30 font-bold",
    },
    other: {
        dot: "bg-stone-600",
        cell: "bg-stone-600 text-white shadow-md shadow-stone-600/30 font-bold",
    },
    present: {
        dot: "bg-emerald-600",
        cell: "bg-emerald-600 text-white shadow-md shadow-emerald-600/30 font-bold",
    },
    notIn: {
        dot: "bg-red-600",
        cell: "bg-red-600 text-white shadow-md shadow-red-600/30 font-bold",
    },
};

export const CALENDAR_LEGEND_ITEMS = [
    { key: "today", label: "Today" },
    { key: "weekOff", label: "Weekly Off" },
    { key: "holiday", label: "Public Holiday" },
    { key: "casual", label: "Casual Leave" },
    { key: "sick", label: "Medical Leave" },
    { key: "compensatory", label: "Comp Off" },
    { key: "lop", label: "Loss of Pay" },
    { key: "present", label: "Present" },
    { key: "notIn", label: "Absent" },
];

export const ATTENDANCE_SUMMARY_STYLES = {
    present: {
        wrap: "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/30",
        text: "text-emerald-700 dark:text-emerald-400",
        badge: "bg-emerald-600",
    },
    notIn: {
        wrap: "bg-red-50 dark:bg-red-950/20 border-red-100 dark:border-red-900/30",
        text: "text-red-700 dark:text-red-400",
        badge: "bg-red-600",
    },
    weekOff: {
        wrap: "bg-orange-50 dark:bg-orange-950/20 border-orange-100 dark:border-orange-900/30",
        text: "text-orange-700 dark:text-orange-400",
        badge: "bg-orange-500",
    },
    holiday: {
        wrap: "bg-fuchsia-50 dark:bg-fuchsia-950/20 border-fuchsia-100 dark:border-fuchsia-900/30",
        text: "text-fuchsia-700 dark:text-fuchsia-400",
        badge: "bg-fuchsia-600",
    },
};

/** Cell style + title for calendar day (priority: today > leave > present > absent > holiday > week off) */
export const getCalendarDayPresentation = (item) => {
    let cellStyle =
        "text-neutral-800 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800";
    let titleText = "";

    if (!item.isCurrentMonth) {
        return {
            cellStyle: "text-neutral-300 dark:text-neutral-600",
            titleText: "",
        };
    }

    if (item.isToday) {
        const statusText =
            item.leave ? "Approved Leave"
            : item.isPresent ? "Present (Checked In)"
            : item.isNotIn ? "Absent (No Check-In)"
            : item.isHoliday ? item.holidayName
            : item.isWeekOff || item.isSunday ? "Weekly Off"
            : "";
        return {
            cellStyle: CALENDAR_COLORS.today.cell,
            titleText: statusText ? `Today - ${statusText}` : "Today",
        };
    }

    if (item.leave) {
        const lType = normalizeLeaveType(item.leave.leaveType);
        const leaveKey =
            lType === "casual" ? "casual"
            : lType === "sick" ? "sick"
            : lType === "lop" ? "lop"
            : lType === "compensatory" ? "compensatory"
            : lType === "emergency" ? "emergency"
            : "other";
        const leaveLabels = {
            casual: "Casual Leave (CL)",
            sick: "Medical Leave (ML)",
            lop: "Loss of Pay (LOP)",
            compensatory: "Comp Off (CO)",
            emergency: "Emergency Leave",
            other: "Leave",
        };
        titleText = `Approved ${leaveLabels[leaveKey]}`;
        cellStyle = CALENDAR_COLORS[leaveKey].cell;
        return { cellStyle, titleText };
    }

    if (item.isPresent) {
        return {
            cellStyle: CALENDAR_COLORS.present.cell,
            titleText: "Present (Checked In)",
        };
    }

    if (item.autoLeaveType && item.autoLeaveType !== "lop") {
        const autoKey = item.autoLeaveType === "sick" || item.autoLeaveType === "carryForwardMedical" ? "sick"
            : item.autoLeaveType === "compensatory" ? "compensatory"
            : "casual";
        const autoLabels = {
            carryForwardCasual: "Compensated with Carry Forward (CL)",
            carryForwardMedical: "Compensated with Carry Forward (ML)",
            casual: "Compensated with Casual Leave",
            sick: "Compensated with Medical Leave",
            compensatory: "Compensated with Comp Off",
        };
        return {
            cellStyle: CALENDAR_COLORS[autoKey].cell,
            titleText: autoLabels[item.autoLeaveType] || autoLabels[autoKey],
        };
    }

    if (item.autoLeaveType === "lop") {
        return {
            cellStyle: CALENDAR_COLORS.lop.cell,
            titleText: "Loss of Pay (LOP)",
        };
    }

    if (item.isNotIn) {
        return {
            cellStyle: CALENDAR_COLORS.notIn.cell,
            titleText: "Absent (No Check-In)",
        };
    }

    if (item.isHoliday) {
        return {
            cellStyle: CALENDAR_COLORS.holiday.cell,
            titleText: item.holidayName,
        };
    }

    if (item.isWeekOff || item.isSunday) {
        return {
            cellStyle: CALENDAR_COLORS.weekOff.cell,
            titleText: "Weekly Off",
        };
    }

    return { cellStyle, titleText };
};
